import React, { Ref, useImperativeHandle, useState } from 'react';
import { CircularProgress } from '@mui/material';

import { ProjectLayoutModel, ProjectSummary } from '@paragon/contract-library';
import {
  createProjectModel,
  ModelProperty,
  projectCreated,
  ProjectSchema,
  PropertyList,
  saveAllProjectChanges,
  validateProjectHierarchyModelHasRequiredProperties,
} from '@paragon/layout-models';
import { getLoggedInUser } from '@paragon/login-tools';
import { getLoggedInUsersOrganizationMaterialSet } from '@paragon/material-lists';
import { InlineNotification } from '@paragon/notification-tools';
import { createScrollbarStyling } from '@paragon/themes';

import { useAppDispatch, useAppSelector } from '../../hooks';

export interface CreateProjectPanelMethods {
  submit: () => Promise<{ success: boolean }>;
}

interface Props {
  ref: Ref<CreateProjectPanelMethods>;

  designUrl: string;
  projectSummaries: readonly ProjectSummary[];
}

/**
 * For use by the `ChooseProjectDialog` only.
 */
export function CreateProjectPanel({ ref, designUrl, projectSummaries }: Props) {
  const dispatch = useAppDispatch();

  const loggedInUser = useAppSelector(getLoggedInUser);
  const materialSet = useAppSelector(getLoggedInUsersOrganizationMaterialSet);
  const materialListsState = useAppSelector((state) => state.materialLists.state);

  const [draftProject, setDraftProject] = useState<ProjectLayoutModel | null>(null);
  const [validationErrors, setValidationErrors] = useState<string>('');

  const existingProjectCount = projectSummaries.length;

  if (!draftProject && loggedInUser && materialSet) {
    const projectModel = createProjectModel(
      (existingProjectCount + 1).toString(),
      materialSet.defaultMaterials,
      loggedInUser,
    );
    setDraftProject(projectModel);
  }

  useImperativeHandle(
    ref,
    () => ({
      async submit() {
        if (draftProject == null) return { success: false };

        const errors = validateProjectHierarchyModelHasRequiredProperties(draftProject);
        if (errors) {
          setValidationErrors(errors);
          return { success: false };
        }

        // This leads to multiple projects in the state, which is suboptimal, but there is not a clear way to
        // save and synchronize the project otherwise. Since the `getProjectLayoutModel` selector relies on the
        // project GUID in the open context, places that seek the active project will be served correctly.
        dispatch(
          projectCreated({
            model: draftProject,
          }),
        );
        await dispatch(saveAllProjectChanges());
        window.location.href = `${designUrl}${draftProject.guid}`;

        return { success: true };
      },
    }),
    [designUrl, dispatch, draftProject, setValidationErrors],
  );

  if (draftProject === null) {
    if (materialListsState === 'pending' || materialListsState === 'loading') {
      return <CircularProgress sx={{ alignSelf: 'center' }} />;
    }

    return <InlineNotification type="error">Failed to create project.</InlineNotification>;
  }

  return (
    <>
      {validationErrors !== '' && <InlineNotification type="error">{validationErrors}</InlineNotification>}
      {projectSummaries.some((projectSummary) => projectSummary.name === draftProject.name) && (
        <InlineNotification type="warning">A project with this name already exists</InlineNotification>
      )}

      <PropertyList sx={[{ marginTop: 1, maxHeight: '600px', overflow: 'auto' }, createScrollbarStyling]}>
        {Object.keys(ProjectSchema.properties).map((propertyName) => (
          <ModelProperty
            property={propertyName as keyof ProjectLayoutModel}
            model={draftProject}
            onEdit={(_, editedProperties) => {
              setDraftProject((oldDraftProject) => {
                if (!oldDraftProject) throw new Error('Draft project must be set.');
                const newDraftProject: ProjectLayoutModel = { ...oldDraftProject };
                for (const [propertyName, newValue] of Object.entries(editedProperties)) {
                  (newDraftProject as any)[propertyName] = newValue;
                }
                return newDraftProject;
              });
            }}
            modelSchema={ProjectSchema}
            key={propertyName}
          />
        ))}
      </PropertyList>
    </>
  );
}
